import {
  ref,
  onMounted,
  defineComponent,
  computed,
  reactive,
} from "@vue/composition-api";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { map as Rmap } from "ramda";
import { JSConn } from "@/utilities/js-conn";
import { format, formatDistance, subDays } from "date-fns";
import TipTapDisplay from "@/components/TipTapDisplay/TipTapDisplay.vue";
import { friendlyTimeStamp } from "@/utilities/display-helpers";
import { itemRouteBuilder, NamedRts } from "@/router";

import { genericSort, sortIcon } from "@/components/helpers";
import { WorkOrderTask } from "@/store/modules/workOrderTasks";
import { filter as Rfilter } from "ramda";
import { buildSubmitButton } from "@/utilities/form-helpers";

export default defineComponent({
  name: "productivityDashboard",
  components: {
    LoadingAnimation,
    TipTapDisplay,
  },

  setup(_props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref(
      "Loading Data... preparing it just for you... Please wait...",
    );

    let from_date = ref("");

    let filter = ref("");
    let sortKey = ref<"name" | "client_field" | "completed_date">(
      "completed_date",
    );
    let sortAsc = ref(false);

    const localSortIcon = (
      lSortKey: "name" | "client_field" | "completed_date",
    ) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const localWorkOrderTasks = ref<WorkOrderTask[]>([]);

    const updateSort = (
      lSortKey: "name" | "client_field" | "completed_date",
    ) => {
      localWorkOrderTasks.value = genericSort(
        [...localWorkOrderTasks.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    let { submitButtonText, submitting } = buildSubmitButton();

    const workOrderTasks = computed(() => {
      let tmpResults = [...localWorkOrderTasks.value];

      if (filter.value !== "") {
        const tmpReg = new RegExp(filter.value, "i");
        tmpResults = Rfilter((item: WorkOrderTask) => {
          let dateResult = false;
          if (item.completed_date) {
            dateResult = tmpReg.test(item.completed_date);
          }
          const nameResult = tmpReg.test(item.name);
          const fieldResult = tmpReg.test(item.client_field);

          return dateResult || nameResult || fieldResult;
        }, tmpResults);
      }

      return tmpResults;
    });

    const showRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.workOrderDetails, id);
    };

    const clientFieldShow = (id: string) => {
      return itemRouteBuilder(NamedRts.clientFieldShow, id);
    };

    const tasksRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.workOrderTasks, id);
    };

    const loadDashboardData = () => {
      const jsConnWorkOrderNotes = new JSConn();
      const jsConnCompletedWorkOrderTasks = new JSConn();
      const jsConnLineItemSummaries = new JSConn();

      jsConnWorkOrderNotes.formParams = {
        from_date: from_date.value,
      };
      jsConnCompletedWorkOrderTasks.formParams = {
        from_date: from_date.value,
      };
      jsConnLineItemSummaries.formParams = {
        from_date: from_date.value,
      };

      const dataPromises = [
        $store.dispatch("needWorkOrderNotes", jsConnWorkOrderNotes),
        $store.dispatch(
          "needCompletedWorkOrderTasks",
          jsConnCompletedWorkOrderTasks,
        ),
        $store.dispatch("needLineItemSummaries", jsConnLineItemSummaries),
      ];

      Promise.all(dataPromises).then(() => {
        localWorkOrderTasks.value = [...apiWorkOrderTasks.value];
        // flip the sort, otherwise on load it would be off
        sortAsc.value = !sortAsc.value;
        updateSort(sortKey.value);
        dataLoaded.value = true;
        submitting.value = false;
        submitButtonText.value = "Submit";
      });
    };

    const lineItemSummaries = computed(() => {
      return $store.getters.lineItemSummaries;
    });

    const submitForm = (evt: any) => {
      evt.preventDefault();
      submitting.value = true;
      submitButtonText.value = "Submitting...";
      // reload the data with the new options
      loadDashboardData();
    };

    onMounted(() => {
      const fromDate = subDays(new Date(), 7);
      from_date.value = format(fromDate, "yyyy-MM-dd");
      loadDashboardData();
    });

    const workOrderNotes = computed(() => {
      const apiNotes = $store.getters.workOrderNotes;
      const rightNow = new Date();
      return Rmap(nt => {
        const createdAt = new Date(nt.created_at);
        const humanizedDate = format(createdAt, "MMMM d, yyyy, h:mm a");
        const time_ago_in_words = formatDistance(rightNow, createdAt) + " ago";
        return {
          ...nt,
          humanized_created_at: humanizedDate,
          time_ago_in_words: time_ago_in_words,
        };
      }, apiNotes);
    });

    const apiWorkOrderTasks = computed(() => {
      return $store.getters.workOrderTasks as WorkOrderTask[];
    });

    return {
      dataLoaded,
      loadingMessage,
      from_date,

      submitForm,

      workOrderNotes,
      workOrderTasks,

      friendlyTimeStamp,

      lineItemSummaries,
      showRouteBuilder,
      clientFieldShow,
      tasksRouteBuilder,

      filter,
      updateSort,
      localSortIcon,

      submitButtonText,
      submitting,
    };
  },
});
